import { ChangeDetectorRef, Component, OnInit, Input, Output, EventEmitter, SimpleChanges } from "@angular/core";
import { ApiService } from "../../services";
import { LoadingScreenService } from "../../shared/loading-screen.service";
import { ModalService } from "../../shared/modal.service";
import { IUserProfile, Roles } from "../../npr-request.model";
import { Comment } from "../../generated-models/Comment";
import { ToastrService } from "ngx-toastr";
import { BaseComponent } from "../../shared/base.component";
import { CapacityOrderRequestStateEnum } from "../../shared/enums/plan-enums";
import { forkJoin } from "rxjs";
@Component({
  standalone: false,
  selector: "plan-comments",
  templateUrl: "./plan-comments.component.html",
  styleUrls: ["../../styles.scss"],
})
export class PlanCommentsComponent extends BaseComponent implements OnInit {
  @Input() serviceTreeId: string;
  @Input() region: string;
  @Input() version: number;
  @Input() blueprint: string;
  // Add new input property for the selected topic
  @Input() selectedTopic: string;
  @Output() commentAdded = new EventEmitter<any>();

  selectedCommentTopic: string;
  commentTopics: string[] = [];
  commentTopicsWithStatus: string[] = [];
  comments: Comment[] = [];
  newComment: string = "";
  placeHolder: string = "Type your comment here...";
  isAdmin: boolean = false;
  isApprover: boolean = false;
  currentUser: string = "";
  userProfile: IUserProfile = null;
  hasAvailableTopicsToAdd: boolean = false;

  // Store all possible topics derived from approvals
  allPossibleTopics: string[] = [];

  get isCcoTopic(): boolean {
    return this.selectedCommentTopic?.startsWith("CCO::") ?? false;
  }

  constructor(
    protected modalService: ModalService,
    protected apiService: ApiService,
    protected loadingService: LoadingScreenService,
    protected notificationService: ToastrService,
    protected cdr: ChangeDetectorRef
  ) {
    super();
  }

  /**
   * Component initialization
   */
  ngOnInit() {
    this.loadingService.setLoading(true);
    this.loadInitialData();
  }

  /**
  * Load all initial data needed for the component
  */
  loadInitialData() {
    if (!this.serviceTreeId || !this.region || this.version === undefined || !this.blueprint) {
      this.notificationService.error("Invalid parameters for loading data.");
      this.loadingService.setLoading(false);
      return;
    }

    // Get user profile first
    this.apiService.getUserProfile()
      .then(profile => {
        this.userProfile = profile;
        if (this.userProfile) {
          this.isAdmin = this.userProfile.IsAdmin || false;
          this.isApprover = this.userProfile.IsApprover || false;
          this.currentUser = this.userProfile.Name || "Anonymous";
        }

        // Now load approvals and comment topics in parallel
        forkJoin({
          approvals: this.apiService.getRegionalPlanApprovals(this.serviceTreeId, this.region, this.version, this.blueprint),
          comments: this.apiService.getCommentTopics(this.serviceTreeId, this.region, this.version, this.blueprint)
        }).subscribe(
          results => {

            // Extract and store all possible topics
            this.allPossibleTopics = Array.from(new Set(results.approvals.map(a => a.Resource)));

            // Store comment topics (format: topic|status)
            this.commentTopicsWithStatus = results.comments.CommentTopics || [];
            this.commentTopics = this.commentTopicsWithStatus.map(topic => topic.split('|')[0]);
            const defaultTopic = results.comments.DefaultCommentTopic;

            // Check approver permissions if needed
            if (this.isApprover && !this.isAdmin) {
              this.checkApproverPermissions();
            }

            // Update available topics status
            this.updateAvailableTopicsStatus();

            // Select default topic or first available topic if present
            if (defaultTopic) {
              this.selectedCommentTopic = defaultTopic;
              this.loadComments();
            } else if (this.commentTopics.length > 0) {
              this.selectedCommentTopic = this.commentTopics[0];
              this.loadComments();
            }

            this.loadingService.setLoading(false);
            this.cdr.detectChanges();
          },
          error => {
            this.notificationService.error(`Error loading initial data: ${error}`);
            this.loadingService.setLoading(false);
            this.cdr.detectChanges();
          }
        );
      })
      .catch(error => {
        this.notificationService.error(`Error loading user profile: ${error}`);
        this.isAdmin = false;
        this.isApprover = false;
        this.currentUser = 'Anonymous';
        this.loadingService.setLoading(false);
        this.cdr.detectChanges();
      });
  }

  /**
   * React to input changes
   */
  ngOnChanges(changes: SimpleChanges) {
    // Handle changes to selectedTopic input property
    if (changes.selectedTopic && changes.selectedTopic.currentValue) {
      const newTopic = changes.selectedTopic.currentValue;

      // Wait for topics to load before processing
      if (this.commentTopics) {
        if (this.commentTopics.includes(newTopic)) {
          // Topic exists - select it and load comments
          this.selectedCommentTopic = newTopic;
          this.loadComments();
        } else if (this.isApprover || this.isAdmin) {
          // Topic doesn't exist but user can create it
          this.openCommentTopicModal(newTopic);
        } else {
          // Non-admin/approver - show notification
          this.notificationService.warning(`Please wait for "${newTopic}" approver to add a comment topic first.`);
        }
      }
    }
  }

  /**
   * Loads the current user profile to determine permissions
   */
  async loadUserProfile() {
    try {
      this.userProfile = await this.apiService.getUserProfile();
      if (this.userProfile) {
        this.isAdmin = this.userProfile.IsAdmin || false;
        this.isApprover = this.userProfile.IsApprover || false;
        this.currentUser = this.userProfile.Name || "Anonymous";
      }
    } catch (error) {
      this.notificationService.error(`Error loading user profile: ${error}`);
      this.isAdmin = false;
      this.isApprover = false;
      this.currentUser = "Anonymous";
    }
  }

  /**
 * Checks if the user has approver permissions for any resources in this plan
 */
  checkApproverPermissions() {
    if (!this.allPossibleTopics || this.allPossibleTopics.length === 0) {
      // No approval resources available
      this.isApprover = false;
      return;
    }

    // Check if user has permissions to approve any of these resources
    const canApproveAny = this.allPossibleTopics.some(resourceType => {
      return this.userProfile.UserRoles?.some(
        role =>
          role.Role === Roles.Administrator || // Admins can approve anything
          (
            (role.Role === Roles.ApproverAdmin || role.Role === Roles.Approver) &&
            role.Types?.toLowerCase().includes(resourceType.toLowerCase())
          )
      );
    });

    // Update isApprover based on whether user can approve any resources
    this.isApprover = canApproveAny;
  }

  /**
   * Checks if comments can be added for the selected topic based on its status
   */
  get canAddComment(): boolean {
    return this.commentTopicsWithStatus.includes(this.selectedCommentTopic + '|' + CapacityOrderRequestStateEnum.CommunicationNeeded);
  }

  /**
  * Updates the status of available topics to add
  */
  updateAvailableTopicsStatus() {
    // Only need to check for approvers/admins
    if (this.isApprover || this.isAdmin) {
      // If admin, they can add any topic
      // If approver, they can only add topics they have permission for
      let approverTopics: string[] = [];

      if (this.isAdmin) {
        // Admins can approve all resources
        approverTopics = [...this.allPossibleTopics];
      } else {
        // For regular approvers, filter resources they have permission for
        approverTopics = this.allPossibleTopics.filter(resource =>
          this.userProfile.UserRoles?.some(
            role => (role.Role === Roles.ApproverAdmin || role.Role === Roles.Approver) &&
              role.Types?.toLowerCase().includes(resource.toLowerCase())
          )
        );
      }

      // Check if there are any topics that:
      // 1. The user can approve
      // 2. Haven't been created yet
      this.hasAvailableTopicsToAdd = approverTopics.some(topic =>
        !this.commentTopics.includes(topic)

      );
    } else {
      this.hasAvailableTopicsToAdd = false;
    }
  }

  /**
  * Loads comment topics for the current plan region
  */
  loadCommentTopics() {
    if (!this.serviceTreeId || !this.region || this.version === undefined || !this.blueprint) {
      this.notificationService.error("Invalid parameters for loading comment topics.");
      return;
    }

    this.loadingService.setLoading(true);
    this.apiService.getCommentTopics(this.serviceTreeId, this.region, this.version, this.blueprint)
      .subscribe(
        (response) => {
          // Store comment topics (format: topic|status)
          this.commentTopicsWithStatus = response.CommentTopics || [];
          this.commentTopics = this.commentTopicsWithStatus.map(topic => topic.split('|')[0]);
          const defaultTopic = response.DefaultCommentTopic;

          // After loading comment topics, check for available topics to add
          this.updateAvailableTopicsStatus();

          this.loadingService.setLoading(false);

          // Select default topic or first available topic if present
          if (defaultTopic) {
            this.selectedCommentTopic = defaultTopic;
            this.loadComments();
          } else if (this.commentTopics.length > 0) {
            this.selectedCommentTopic = this.commentTopics[0];
            this.loadComments();
          }
          // If no topics are available, the UI will show appropriate message
        },
        (error) => {
          this.notificationService.error(`Error loading comment topics: ${error}`);
          this.loadingService.setLoading(false);
        }
      );
  }

  /**
 * Loads comments for the selected topic
 */
  loadComments() {
    if (!this.serviceTreeId || !this.region || this.version === undefined || !this.blueprint || !this.selectedCommentTopic) {
      this.notificationService.error("Invalid parameters for loading comments.");
      return;
    }

    this.loadingService.setLoading(true);
    this.apiService.getComments(this.serviceTreeId, this.region, this.version, this.blueprint, this.selectedCommentTopic)
      .subscribe(
        (comments) => {
          // Reverse chronological order: latest comment first
          this.comments = comments.sort(
            (a, b) => new Date(b.At).getTime() - new Date(a.At).getTime()
          );
          this.loadingService.setLoading(false);
        },
        (error) => {
          this.notificationService.error(`Error loading comments: ${error}`);
          this.loadingService.setLoading(false);
        }
      );
  }

  /**
   * Validates if the current comment is valid for submission
   */
  isValidComment() {
    if (this.newComment) {
      return this.newComment.trim().length != 0;
    }
    return false;
  }

  /**
  * Opens the modal to add a new comment topic
  *
  * @param specificTopic Optional specific topic to use or pre-select
  */
  openCommentTopicModal(specificTopic?: string) {
    // Only approvers and admins can add new comment topics
    if (!this.isApprover && !this.isAdmin) {
      if (specificTopic) {
        this.notificationService.warning(`Please wait for "${specificTopic}" approver to add a comment topic first.`);
      } else {
        this.notificationService.warning("Only approvers or admins can add new comment topics.");
      }
      return;
    }

    // Filter available topics based on permissions and already created topics
    let availableTopics: string[] = [];

    if (specificTopic) {
      // For specific topic, check if user has permission (for non-admins)
      if (!this.isAdmin) {
        const hasPermission = this.userProfile.UserRoles?.some(
          role => (role.Role === Roles.ApproverAdmin || role.Role === Roles.Approver) &&
            role.Types?.toLowerCase().includes(specificTopic.toLowerCase())
        );

        if (!hasPermission) {
          this.notificationService.warning(`Please wait for "${specificTopic}" approver to add a comment topic first.`);
          return;
        }
      }

      // Using specific topic
      availableTopics = [specificTopic];
    } else {
      // No specific topic - use permissions to filter from all possible topics
      if (this.isAdmin) {
        // Admins can add any topic that hasn't been created yet
        availableTopics = this.allPossibleTopics.filter(topic =>
          !this.commentTopics.includes(topic)
        );
      } else {
        // Regular approvers can only add topics they have permission for
        availableTopics = this.allPossibleTopics.filter(topic =>
          // Topic hasn't been created yet
          !this.commentTopics.includes(topic) &&
          // User has permission for this topic
          this.userProfile.UserRoles?.some(
            role => (role.Role === Roles.ApproverAdmin || role.Role === Roles.Approver) &&
              role.Types?.toLowerCase().includes(topic.toLowerCase())
          )
        );
      }

      // Check if there are any available topics
      if (availableTopics.length === 0) {
        this.notificationService.warning("No available comment topics to add. Either all topics have been created or you don't have permission to add any more topics.");
        return;
      }
    }

    // Open modal with filtered available topics
    this.modalService.openCommentTopicModal(
      availableTopics,
      this.serviceTreeId,
      this.region,
      this.version,
      this.blueprint,
      this.currentUser,
      specificTopic // This will be undefined if no specific topic was provided
    ).then(this.handleCommentTopicCreationResult.bind(this));
  }

  /**
  * Handles the result from the comment topic modal
  *
  * @param result The modal result
  */
  private handleCommentTopicCreationResult(result: { topic: string; comment: string } | null) {
    if (!result) {
      return; // Modal was canceled
    }

    const comment: Comment = {
      Version: this.version,
      Topic: result.topic,
      Comment: result.comment,
      By: this.currentUser,
      At: new Date()
    };

    this.apiService.addComment(this.serviceTreeId, this.region, this.version, this.blueprint, comment).subscribe(
      () => {
        // Reload comment topics after adding a new topic
        this.loadCommentTopics();

        // Notify parent component
        this.commentAdded.emit(comment);

        // Select the newly created topic
        if (result.topic && this.commentTopics.indexOf(result.topic) === -1) {
          this.commentTopics.push(result.topic);
        }
        this.selectedCommentTopic = result.topic;
        this.loadComments();
      },
      (error) => {
        this.notificationService.error(`Error adding comment: ${error}`);
      }
    );
  }

  /**
  * Saves a new comment to the selected topic
  */
  saveNewComment() {
    if (!this.isValidComment()) {
      return;
    }

    this.loadingService.setLoading(true);
    const comment: Comment = {
      Version: this.version,
      Topic: this.selectedCommentTopic,
      Comment: this.newComment.trim(),
      By: this.currentUser,
      At: new Date()
    };

    this.apiService.addComment(this.serviceTreeId, this.region, this.version, this.blueprint, comment).subscribe(
      () => {
        // Reload comments
        this.loadComments();

        // Clear input
        this.newComment = "";

        // Notify parent component
        this.commentAdded.emit(comment);

        this.loadingService.setLoading(false);
      },
      (error) => {
        this.notificationService.error(`Error adding comment: ${error}`);
        this.loadingService.setLoading(false);
      }
    );
  }
}
